/**
 * Redux actions for the Review feature.
 *
 * Playbook CRUD: /playbooks/*
 * Review execution: /reviews/*
 * Collaboration: /reviews/<uuid>/collaborators/*
 * Audit log: /reviews/<uuid>/audit-log
 * Document viewer: /reviews/<uuid>/documents/<id>
 * Re-review: /reviews/<uuid>/re-review
 */

import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import axios from 'axios';
import apis from '../../../utils/apis';
import { showFeedbackModal } from '../UserFeedbackActions';
import { describeUploadResult, runSequentialUpload } from '../../../services/uploads/uploadBatching';
import { partitionOversized, oversizedMessage } from '../../../utils/uploadLimits';
import { RootState } from '../..';
import { translate } from '../../../i18n/translate';
import {
  CreateReviewPayload,
  UpdateReviewPayload,
  CreateClauseRulePayload,
  UpdateClauseRulePayload,
  CreateGlobalRulePayload,
  UpdateGlobalRulePayload,
  StartReviewPayload,
  UpdateFindingDecisionPayload,
  FindingSeverity,
  AddCollaboratorPayload,
  UpdateCollaboratorPayload,
  SubmitSignOffPayload,
} from '../../../types/review';
import { readErrorMessage, toErrorText } from '../../../utils/errorMessage';
// ─── Action Types ────────────────────────────────────────────────────────────

export const REVIEW_LOADING = 'REVIEW_LOADING';
export const REVIEW_FETCH_LIST = 'REVIEW_FETCH_LIST';
export const REVIEW_APPEND_LIST = 'REVIEW_APPEND_LIST';
export const REVIEW_SET_DETAIL = 'REVIEW_SET_DETAIL';
export const REVIEW_LOADING_DETAIL = 'REVIEW_LOADING_DETAIL';
export const REVIEW_UPDATE_IN_LIST = 'REVIEW_UPDATE_IN_LIST';
export const REVIEW_REMOVE_FROM_LIST = 'REVIEW_REMOVE_FROM_LIST';
export const REVIEW_SET_SESSION = 'REVIEW_SET_SESSION';
export const REVIEW_LOADING_SESSION = 'REVIEW_LOADING_SESSION';
export const REVIEW_SET_FINDINGS = 'REVIEW_SET_FINDINGS';
export const REVIEW_APPEND_FINDINGS = 'REVIEW_APPEND_FINDINGS';
export const REVIEW_LOADING_FINDINGS = 'REVIEW_LOADING_FINDINGS';
export const REVIEW_SET_UPLOADING = 'REVIEW_SET_UPLOADING';
export const REVIEW_EXTRACTING_PLAYBOOK = 'REVIEW_EXTRACTING_PLAYBOOK';
export const REVIEW_ERROR = 'REVIEW_ERROR';

// Sessions list
export const REVIEW_LOADING_SESSIONS = 'REVIEW_LOADING_SESSIONS';
export const REVIEW_FETCH_SESSIONS = 'REVIEW_FETCH_SESSIONS';
export const REVIEW_APPEND_SESSIONS = 'REVIEW_APPEND_SESSIONS';
export const REVIEW_REMOVE_SESSION_FROM_LIST = 'REVIEW_REMOVE_SESSION_FROM_LIST';
export const REVIEW_BULK_REMOVE_SESSIONS = 'REVIEW_BULK_REMOVE_SESSIONS';
export const REVIEW_RENAME_SESSION_IN_LIST = 'REVIEW_RENAME_SESSION_IN_LIST';
export const REVIEW_RESTORE_SESSION = 'REVIEW_RESTORE_SESSION';

// Real-time updates (dispatched from PubSub hooks)
export const REVIEW_FINDING_RECEIVED = 'REVIEW_FINDING_RECEIVED';
export const REVIEW_DOCUMENT_COMPLETED = 'REVIEW_DOCUMENT_COMPLETED';
export const REVIEW_REVIEW_COMPLETED = 'REVIEW_REVIEW_COMPLETED';
export const REVIEW_SESSION_PROGRESS = 'REVIEW_SESSION_PROGRESS';

// Playbook extraction progress (decoupled from template state)
export const REVIEW_EXTRACT_CONNECTING = 'REVIEW_EXTRACT_CONNECTING';
export const REVIEW_EXTRACT_CONNECTED = 'REVIEW_EXTRACT_CONNECTED';
export const REVIEW_EXTRACT_STEPS = 'REVIEW_EXTRACT_STEPS';
export const REVIEW_EXTRACT_STEP = 'REVIEW_EXTRACT_STEP';
export const REVIEW_EXTRACT_COMPLETED = 'REVIEW_EXTRACT_COMPLETED';
export const REVIEW_EXTRACT_FAILED = 'REVIEW_EXTRACT_FAILED';
export const REVIEW_EXTRACT_RESET = 'REVIEW_EXTRACT_RESET';

// Collaborator management
export const REVIEW_SET_COLLABORATORS = 'REVIEW_SET_COLLABORATORS';
export const REVIEW_LOADING_COLLABORATORS = 'REVIEW_LOADING_COLLABORATORS';
export const REVIEW_ADD_COLLABORATOR = 'REVIEW_ADD_COLLABORATOR';
export const REVIEW_UPDATE_COLLABORATOR = 'REVIEW_UPDATE_COLLABORATOR';
export const REVIEW_REMOVE_COLLABORATOR = 'REVIEW_REMOVE_COLLABORATOR';

// Audit log
export const REVIEW_SET_AUDIT_LOG = 'REVIEW_SET_AUDIT_LOG';
export const REVIEW_APPEND_AUDIT_LOG = 'REVIEW_APPEND_AUDIT_LOG';
export const REVIEW_LOADING_AUDIT_LOG = 'REVIEW_LOADING_AUDIT_LOG';

// Document viewer
export const REVIEW_SET_DOCUMENT_CONTENT = 'REVIEW_SET_DOCUMENT_CONTENT';
export const REVIEW_LOADING_DOCUMENT = 'REVIEW_LOADING_DOCUMENT';

// Live collaboration PubSub events
export const REVIEW_FINDING_DECISION_RECEIVED = 'REVIEW_FINDING_DECISION_RECEIVED';
export const REVIEW_COLLABORATOR_EVENT = 'REVIEW_COLLABORATOR_EVENT';

// Decision summary update
export const REVIEW_SET_DECISION_SUMMARY = 'REVIEW_SET_DECISION_SUMMARY';

// Update finding in-place (for live decisions)
export const REVIEW_UPDATE_FINDING = 'REVIEW_UPDATE_FINDING';
export const REVIEW_ADD_FINDING_NOTE = 'REVIEW_ADD_FINDING_NOTE';
export const REVIEW_UPDATE_FINDING_NOTE = 'REVIEW_UPDATE_FINDING_NOTE';
export const REVIEW_DELETE_FINDING_NOTE = 'REVIEW_DELETE_FINDING_NOTE';
export const REVIEW_BULK_UPDATE_FINDINGS = 'REVIEW_BULK_UPDATE_FINDINGS';

// Sign-off & finalization
export const REVIEW_SET_SIGN_OFF_STATUS = 'REVIEW_SET_SIGN_OFF_STATUS';
export const REVIEW_LOADING_SIGN_OFF = 'REVIEW_LOADING_SIGN_OFF';
export const REVIEW_SESSION_STATUS_CHANGE = 'REVIEW_SESSION_STATUS_CHANGE';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const handleApiError = (
  error: any,
  dispatch: ThunkDispatch<RootState, undefined, AnyAction>,
  customMessage?: string,
) => {
  // Skip cancelled requests (includes licensing errors converted to Cancel by interceptor)
  if (axios.isCancel(error)) return;

  const response = error?.response?.data || {};
  const message =
    customMessage ||
    toErrorText(response.error) || 
    response.message ||
    'Something went wrong. Please try again.';

  dispatch(
    showFeedbackModal({
      showModal: true,
      message,
      modalType: 'error',
      duration: 5000,
    }),
  );

  dispatch({ type: REVIEW_ERROR, payload: message });
};

// ─── Review CRUD ─────────────────────────────────────────────────────────────

interface FetchReviewsParams {
  page?: number;
  page_size?: number;
  status?: string;
  search?: string;
}

export const fetchReviews = (params: FetchReviewsParams = {}) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    const { page = 1, page_size = 20, status, search } = params;

    dispatch({ type: REVIEW_LOADING, payload: true });

    try {
      const response = await apis.get('/playbooks', {
        params: {
          page,
          page_size,
          ...(status ? { status } : {}),
          ...(search ? { search } : {}),
        },
      });

      if (response.status === 200) {
        const data = response.data;
        dispatch({
          type: page === 1 ? REVIEW_FETCH_LIST : REVIEW_APPEND_LIST,
          payload: {
            items: data.items || [],
            total: data.total || 0,
            page: data.page || page,
            total_pages: data.total_pages || 1,
          },
        });
        return data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      dispatch({ type: REVIEW_LOADING, payload: false });
    }

    return null;
  };
};

/**
 * @param options.silent Refresh WITHOUT raising `isLoadingDetail`.
 *   The detail page swaps its whole canvas for a skeleton while that flag is
 *   up, so a background re-read - the extraction status poll, the refetch that
 *   follows a completed run - must not raise it, or the page blinks out from
 *   under whatever the user is watching.
 */
export const fetchReviewDetail = (reviewUuid: string, options?: { silent?: boolean }) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    if (!reviewUuid) return null;

    const silent = options?.silent === true;
    if (!silent) dispatch({ type: REVIEW_LOADING_DETAIL, payload: true });

    try {
      const response = await apis.get(`/playbooks/${reviewUuid}`);

      if (response.status === 200) {
        dispatch({ type: REVIEW_SET_DETAIL, payload: response.data });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      if (!silent) dispatch({ type: REVIEW_LOADING_DETAIL, payload: false });
    }

    return null;
  };
};

export const createReview = (payload: CreateReviewPayload) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/playbooks', payload);

      if (response.status === 201) {
        // dispatch(
        //   showFeedbackModal({
        //     showModal: true,
        //     message: 'Playbook created successfully',
        //     modalType: 'success',
        //     duration: 3000,
        //   }),
        // );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to create review');
    }

    return null;
  };
};

export const updateReview = (
  reviewUuid: string,
  payload: UpdateReviewPayload,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(`/playbooks/${reviewUuid}`, payload);

      if (response.status === 200) {
        dispatch({ type: REVIEW_UPDATE_IN_LIST, payload: response.data });
        dispatch({ type: REVIEW_SET_DETAIL, payload: response.data });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Review updated'),
            modalType: 'success',
            duration: 2000,
          }),
        );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update review');
    }

    return null;
  };
};

export const deleteReview = (reviewUuid: string, deleteSessions: boolean = false) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const params = deleteSessions ? { delete_sessions: 'true' } : {};
      const response = await apis.delete(`/playbooks/${reviewUuid}`, { params });

      if (response.status === 200) {
        dispatch({ type: REVIEW_REMOVE_FROM_LIST, payload: reviewUuid });
        const sessionsDeleted = response.data?.sessions_deleted || 0;
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: sessionsDeleted > 0
              ? translate('Playbook deleted along with {{count}} review session(s)', { count: sessionsDeleted })
              : translate('Playbook deleted'),
            modalType: 'success',
            duration: 3000,
          }),
        );
        return true;
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const errorMsg = readErrorMessage(error, '');

      if (status === 403) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: errorMsg || 'You do not have permission to delete this playbook. Admin access is required.',
            modalType: 'error',
            duration: 5000,
          }),
        );
      } else {
        handleApiError(error, dispatch, 'Failed to delete playbook');
      }
    }

    return false;
  };
};

// ─── Clause Rules ────────────────────────────────────────────────────────────

export const addClauseRule = (
  reviewUuid: string,
  payload: CreateClauseRulePayload,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(
        `/playbooks/${reviewUuid}/clause-rules`,
        payload,
      );

      if (response.status === 201) {
        // Refresh detail to pick up new rule
        dispatch(fetchReviewDetail(reviewUuid));
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Clause rule added'),
            modalType: 'success',
            duration: 2000,
          }),
        );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to add clause rule');
    }

    return null;
  };
};

export const updateClauseRule = (
  reviewUuid: string,
  ruleUuid: string,
  payload: UpdateClauseRulePayload,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(
        `/playbooks/${reviewUuid}/clause-rules/${ruleUuid}`,
        payload,
      );

      if (response.status === 200) {
        dispatch(fetchReviewDetail(reviewUuid));
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Clause rule updated'),
            modalType: 'success',
            duration: 2000,
          }),
        );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update clause rule');
    }

    return null;
  };
};

export const deleteClauseRule = (reviewUuid: string, ruleUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(
        `/playbooks/${reviewUuid}/clause-rules/${ruleUuid}`,
      );

      if (response.status === 200) {
        dispatch(fetchReviewDetail(reviewUuid));
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Clause rule removed'),
            modalType: 'success',
            duration: 2000,
          }),
        );
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to delete clause rule');
    }

    return false;
  };
};

// ─── Global Rules ────────────────────────────────────────────────────────────

export const addGlobalRule = (
  reviewUuid: string,
  payload: CreateGlobalRulePayload,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(
        `/playbooks/${reviewUuid}/global-rules`,
        payload,
      );

      if (response.status === 201) {
        dispatch(fetchReviewDetail(reviewUuid));
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Global rule added'),
            modalType: 'success',
            duration: 2000,
          }),
        );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to add global rule');
    }

    return null;
  };
};

export const updateGlobalRule = (
  reviewUuid: string,
  ruleUuid: string,
  payload: UpdateGlobalRulePayload,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(
        `/playbooks/${reviewUuid}/global-rules/${ruleUuid}`,
        payload,
      );

      if (response.status === 200) {
        dispatch(fetchReviewDetail(reviewUuid));
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Global rule updated'),
            modalType: 'success',
            duration: 2000,
          }),
        );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update global rule');
    }

    return null;
  };
};

export const deleteGlobalRule = (reviewUuid: string, ruleUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(
        `/playbooks/${reviewUuid}/global-rules/${ruleUuid}`,
      );

      if (response.status === 200) {
        dispatch(fetchReviewDetail(reviewUuid));
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Global rule removed'),
            modalType: 'success',
            duration: 2000,
          }),
        );
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to delete global rule');
    }

    return false;
  };
};

// ─── Review Sessions ─────────────────────────────────────────────────────────

/**
 * Start a review: create the session, upload its documents, then analyze.
 *
 * This used to be ONE multipart POST to `/reviews` carrying the playbook, the
 * settings AND every contract - with no file cap at all, so a partner dropping
 * a folder of thirty agreements produced the biggest body in the product. On
 * the EU edge that body is reset and the user gets nothing.
 *
 * Now: `/reviews/create` from metadata, `/reviews/<uuid>/documents` in bounded
 * sequential batches, `/reviews/<uuid>/analyze` once they have landed. A reset
 * costs one batch instead of the whole review, and the session survives it.
 *
 * The review's live progress arrives on the BARE session uuid group - reviews
 * do NOT key their group on the correlation id the way matters and data
 * sources do - so `ReviewSessionPage` joining on navigation is already right,
 * and there is no group to thread through from here.
 */
export const startReview = (
  playbookUuid: string,
  payload: StartReviewPayload,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: REVIEW_SET_UPLOADING, payload: true });

    try {
      const { accepted, oversized } = partitionOversized(payload.files);
      if (!accepted.length) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('No documents could be uploaded'),
            subMsg: oversizedMessage(oversized),
            modalType: 'error',
            duration: 8000,
          }),
        );
        return null;
      }

      // ── 1. The session, from metadata alone ──
      const body: Record<string, any> = { playbook_uuid: playbookUuid };
      if (payload.name?.trim()) body.name = payload.name.trim();
      if (payload.jurisdiction?.length) body.jurisdiction = payload.jurisdiction;
      if (payload.matter_uuid) body.matter_uuid = payload.matter_uuid;
      if (payload.data_source_ids?.length) body.data_source_ids = payload.data_source_ids;
      if (payload.language) body.language = payload.language;
      if (payload.collaborators?.length) body.collaborators = payload.collaborators;

      const created = await apis.post('/reviews/create', body);
      if (created.status !== 200 && created.status !== 201) {
        handleApiError(created, dispatch, 'Failed to start review');
        return null;
      }

      const session = created.data?.session ?? created.data;
      const reviewUuid =
        session?.session_uuid || session?.review_uuid || session?.uuid ||
        created.data?.session_uuid || created.data?.review_uuid;

      if (!reviewUuid) {
        handleApiError(created, dispatch, 'Review created but no session id was returned');
        return null;
      }

      // The session exists from here on, so every later failure is partial,
      // not total - the user keeps a review they can add documents to.
      dispatch({ type: REVIEW_SET_SESSION, payload: created.data });

      // ── 2. The documents, in bounded sequential batches ──
      const upload = await runSequentialUpload(accepted, {
        send: (batch) => {
          const form = new FormData();
          batch.forEach((file) => form.append('files', file));
          return apis.post(`/reviews/${reviewUuid}/documents`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        },
      });

      const problems = [
        oversized.length ? oversizedMessage(oversized) : null,
        describeUploadResult(upload),
      ].filter(Boolean) as string[];

      if (upload.uploadedCount === 0) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('The review was created, but none of its documents could be uploaded'),
            subMsg: problems.join(' ') || undefined,
            modalType: 'error',
            duration: 10000,
          }),
        );
        return { ...created.data, session_uuid: reviewUuid, upload };
      }

      // ── 3. Analysis, as its own call ──
      const analyzed = await apis.post(`/reviews/${reviewUuid}/analyze`, {});

      dispatch(
        showFeedbackModal({
          showModal: true,
          message: problems.length
            ? translate('Review started with {{done}} of {{total}} documents', { done: upload.uploadedCount, total: payload.files.length })
            : translate('Review started - documents are being processed'),
          subMsg: problems.join(' ') || undefined,
          modalType: problems.length ? 'warning' : 'success',
          duration: problems.length ? 10000 : 4000,
        }),
      );

      return {
        ...created.data,
        ...(analyzed.data || {}),
        session_uuid: reviewUuid,
        upload,
      };
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to start review');
    } finally {
      dispatch({ type: REVIEW_SET_UPLOADING, payload: false });
    }

    return null;
  };
};

export const deleteReviewSession = (reviewUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(`/reviews/${reviewUuid}`);
      if (response.status === 200) {
        dispatch({ type: REVIEW_REMOVE_SESSION_FROM_LIST, payload: reviewUuid });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Review session deleted'),
            modalType: 'success',
            duration: 3000,
          }),
        );
        return { success: true };
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const errorMsg = readErrorMessage(error, '');

      if (status === 403) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: errorMsg || 'You do not have permission to delete this review. Admin access is required.',
            modalType: 'error',
            duration: 5000,
          }),
        );
      } else {
        handleApiError(error, dispatch, 'Failed to delete review session');
      }
      return { success: false, error: errorMsg || 'Failed to delete', status };
    }
    return { success: false };
  };
};

export const bulkDeleteReviewSessions = (uuids: string[]) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/reviews/bulk-delete', { uuids });
      if (response.status === 200) {
        dispatch({ type: REVIEW_BULK_REMOVE_SESSIONS, payload: uuids });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: `${response.data.deleted_count || response.data.deleted || uuids.length} review session(s) deleted`,
            modalType: 'success',
            duration: 3000,
          }),
        );
        return { success: true, deleted: response.data.deleted_count || response.data.deleted || uuids.length };
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const errorMsg = readErrorMessage(error, '');

      if (status === 403) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: errorMsg || 'You do not have permission to delete reviews. Admin access is required.',
            modalType: 'error',
            duration: 5000,
          }),
        );
      } else {
        handleApiError(error, dispatch, 'Failed to delete review sessions');
      }
      return { success: false, error: errorMsg || 'Failed to delete', status };
    }
    return { success: false };
  };
};

export const restoreReviewSession = (reviewUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/reviews/${reviewUuid}/restore`);
      if (response.status === 200) {
        dispatch({
          type: REVIEW_RESTORE_SESSION,
          payload: reviewUuid,
        });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Review session restored'),
            modalType: 'success',
            duration: 3000,
          }),
        );
        return { success: true };
      }
    } catch (error: any) {
      const errorMsg = readErrorMessage(error, '');
      handleApiError(error, dispatch, 'Failed to restore review session');
      return { success: false, error: errorMsg || 'Failed to restore' };
    }
    return { success: false };
  };
};

export const renameReviewSession = (reviewUuid: string, name: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.patch(`/reviews/${reviewUuid}`, { name });
      if (response.status === 200) {
        dispatch({
          type: REVIEW_RENAME_SESSION_IN_LIST,
          payload: { uuid: reviewUuid, name: response.data.name },
        });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Review session renamed'),
            modalType: 'success',
            duration: 3000,
          }),
        );
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to rename review session');
    }
    return false;
  };
};

export const fetchReviewSession = (
  reviewUuid: string,
  sessionUuid: string,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    if (!sessionUuid) return null;

    dispatch({ type: REVIEW_LOADING_SESSION, payload: true });

    try {
      const response = await apis.get(
        `/reviews/${sessionUuid}`,
      );

      if (response.status === 200) {
        dispatch({ type: REVIEW_SET_SESSION, payload: response.data });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      dispatch({ type: REVIEW_LOADING_SESSION, payload: false });
    }

    return null;
  };
};

// ─── Findings ────────────────────────────────────────────────────────────────

interface FetchFindingsParams {
  page?: number;
  page_size?: number;
  severity?: FindingSeverity;
  decision?: string;
}

export const fetchFindings = (
  reviewUuid: string,
  sessionUuid: string,
  params: FetchFindingsParams = {},
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    const { page = 1, page_size = 50, severity, decision } = params;

    dispatch({ type: REVIEW_LOADING_FINDINGS, payload: true });

    try {
      const response = await apis.get(
        `/reviews/${sessionUuid}/findings`,
        {
          params: {
            page,
            page_size,
            ...(severity ? { severity } : {}),
            ...(decision ? { decision } : {}),
          },
        },
      );

      if (response.status === 200) {
        const data = response.data;
        dispatch({
          type: page === 1 ? REVIEW_SET_FINDINGS : REVIEW_APPEND_FINDINGS,
          payload: {
            findings: data.findings || [],
            total: data.total || 0,
            page: data.page || page,
            total_pages: data.total_pages || 1,
          },
        });
        if (data.decision_summary) {
          dispatch({ type: REVIEW_SET_DECISION_SUMMARY, payload: data.decision_summary });
        }
        return data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      dispatch({ type: REVIEW_LOADING_FINDINGS, payload: false });
    }

    return null;
  };
};

export const updateFindingDecision = (
  reviewUuid: string,
  sessionUuid: string,
  findingUuid: string,
  payload: UpdateFindingDecisionPayload,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(
        `/reviews/${sessionUuid}/findings/${findingUuid}`,
        payload,
      );

      if (response.status === 200) {
        // Update the finding in-place rather than refetching all
        dispatch({
          type: REVIEW_UPDATE_FINDING,
          payload: {
            uuid: findingUuid,
            decision: payload.decision,
            decision_by_name: response.data.decision_by_name,
            decision_at: response.data.decision_at,
            decision_notes: payload.notes || null,
            modified_text: payload.modified_text || null,
          },
        });

        // Update session risk score if returned
        if (response.data.overall_risk_score !== undefined && response.data.overall_risk_score !== null) {
          dispatch({
            type: REVIEW_REVIEW_COMPLETED,
            payload: {
              overall_risk_score: response.data.overall_risk_score,
            },
          });
        }

        // If backend auto-transitioned to pending_sign_off
        if (response.data.session_status) {
          dispatch({
            type: REVIEW_SESSION_STATUS_CHANGE,
            payload: { status: response.data.session_status },
          });
        }

        dispatch(
          showFeedbackModal({
            showModal: true,
            message: `Finding marked as ${payload.decision}`,
            modalType: 'success',
            duration: 2000,
          }),
        );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update finding decision');
    }

    return null;
  };
};

export const addFindingNote = (
  sessionUuid: string,
  findingUuid: string,
  content: string,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(
        `/reviews/${sessionUuid}/findings/${findingUuid}/notes`,
        { content },
      );
      if (response.status === 201 || response.status === 200) {
        dispatch({
          type: REVIEW_ADD_FINDING_NOTE,
          payload: { findingUuid, note: response.data },
        });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to add note');
    }
    return null;
  };
};

export const updateFindingNote = (
  sessionUuid: string,
  findingUuid: string,
  noteUuid: string,
  content: string,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(
        `/reviews/${sessionUuid}/findings/${findingUuid}/notes/${noteUuid}`,
        { content },
      );
      if (response.status === 200) {
        dispatch({
          type: REVIEW_UPDATE_FINDING_NOTE,
          payload: { findingUuid, note: response.data },
        });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update note');
    }
    return null;
  };
};

export const deleteFindingNote = (
  sessionUuid: string,
  findingUuid: string,
  noteUuid: string,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(
        `/reviews/${sessionUuid}/findings/${findingUuid}/notes/${noteUuid}`,
      );
      if (response.status === 200) {
        dispatch({
          type: REVIEW_DELETE_FINDING_NOTE,
          payload: { findingUuid, noteUuid },
        });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to delete note');
    }
    return null;
  };
};

export const undoFindingDecision = (
  sessionUuid: string,
  findingUuid: string,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(
        `/reviews/${sessionUuid}/findings/${findingUuid}`,
        { decision: null },
      );

      if (response.status === 200) {
        dispatch({
          type: REVIEW_UPDATE_FINDING,
          payload: {
            uuid: findingUuid,
            decision: null,
            decision_by_name: null,
            decision_at: null,
            decision_notes: null,
            modified_text: null,
            is_undo: true,
          },
        });

        if (response.data.overall_risk_score !== undefined && response.data.overall_risk_score !== null) {
          dispatch({
            type: REVIEW_REVIEW_COMPLETED,
            payload: { overall_risk_score: response.data.overall_risk_score },
          });
        }

        if (response.data.session_status) {
          dispatch({
            type: REVIEW_SESSION_STATUS_CHANGE,
            payload: { status: response.data.session_status },
          });
        }

        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Decision undone - finding is back to pending'),
            modalType: 'success',
            duration: 2000,
          }),
        );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to undo finding decision');
    }
    return null;
  };
};

/**
 * Bulk update finding decisions (accept/reject all filtered findings).
 */
export const bulkUpdateFindingDecisions = (
  sessionUuid: string,
  decision: 'accepted' | 'rejected',
  findingUuids?: string[],
  notes?: string,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const payload: any = { decision };
      if (findingUuids && findingUuids.length > 0) {
        payload.finding_uuids = findingUuids;
      }
      if (notes) {
        payload.notes = notes;
      }

      const response = await apis.put(
        `/reviews/${sessionUuid}/findings/bulk-decision`,
        payload,
      );

      if (response.status === 200) {
        dispatch({
          type: REVIEW_BULK_UPDATE_FINDINGS,
          payload: {
            finding_uuids: response.data.finding_uuids || findingUuids || [],
            decision,
            decision_by_name: response.data.decision_by_name,
            decision_at: response.data.decision_at,
            updated: response.data.updated,
          },
        });

        // Update risk score locally if returned
        if (response.data.overall_risk_score !== undefined && response.data.overall_risk_score !== null) {
          dispatch({
            type: REVIEW_REVIEW_COMPLETED,
            payload: { overall_risk_score: response.data.overall_risk_score },
          });
        }

        if (response.data.session_status) {
          dispatch({
            type: REVIEW_SESSION_STATUS_CHANGE,
            payload: { status: response.data.session_status },
          });
        }

        dispatch(
          showFeedbackModal({
            showModal: true,
            message: `${response.data.updated} findings marked as ${decision}`,
            modalType: 'success',
            duration: 3000,
          }),
        );

        // Re-fetch full session to sync risk score, counts, and status
        dispatch(fetchReviewSession(sessionUuid, sessionUuid) as any);

        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to bulk update finding decisions');
    }
    return null;
  };
};

// ─── Playbook Extraction ─────────────────────────────────────────────────────

/**
 * Stream the playbook's source document binary from the backend.
 * Returns a Blob that can be used with URL.createObjectURL for inline viewing.
 */
export const streamPlaybookSourceDocument = (
  playbookUuid: string,
  disposition: 'inline' | 'attachment' = 'inline',
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis({
        method: 'POST',
        url: `/playbooks/${playbookUuid}/stream-source`,
        data: { disposition },
        responseType: 'blob',
      });

      if (response.status === 200) {
        const contentType =
          response.headers['content-type'] || 'application/octet-stream';
        return { success: true, blob: response.data as Blob, contentType };
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to stream playbook document');
    }
    return { success: false, blob: null, contentType: '' };
  };
};

/**
 * Stream ONE of the playbook's source documents by its own uuid.
 *
 * The playbook-wide `stream-source` above can only ever hand back the single
 * legacy document; a playbook extracted from five files needs the document
 * addressed. Rows the backend marks `is_legacy` have no uuid and still go
 * through `streamPlaybookSourceDocument`.
 */
export const streamPlaybookSourceDocumentById = (
  playbookUuid: string,
  documentUuid: string,
  disposition: 'inline' | 'attachment' = 'inline',
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis({
        method: 'GET',
        url: `/playbooks/${playbookUuid}/source-documents/${documentUuid}/stream`,
        params: { disposition },
        responseType: 'blob',
      });

      if (response.status === 200) {
        const contentType =
          response.headers['content-type'] || 'application/octet-stream';
        return { success: true, blob: response.data as Blob, contentType };
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to stream playbook document');
    }
    return { success: false, blob: null, contentType: '' };
  };
};

/**
 * Swap ONE source document for a new file, leaving the playbook's rules alone.
 *
 * Distinct from `extractPlaybook('replace')`, which throws the rules away and
 * regenerates them from scratch: this only corrects the provenance row - the
 * wrong revision was uploaded, a scan was unreadable, a name was wrong. The
 * backend returns the updated row; the playbook is refetched so the Details
 * list, the count and the context pane all move together rather than each
 * patching their own copy.
 *
 * A legacy row has no uuid to address - callers route those to the
 * playbook-wide replace-and-re-extract path instead.
 */
export const replacePlaybookSourceDocument = (
  playbookUuid: string,
  documentUuid: string,
  file: File,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apis.post(
        `/playbooks/${playbookUuid}/source-documents/${documentUuid}/replace`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      if (response.status === 200 || response.status === 201) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Document replaced'),
            modalType: 'success',
            duration: 3000,
          }),
        );
        await dispatch(fetchReviewDetail(playbookUuid, { silent: true }) as any);
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to replace the source document');
    }
    return null;
  };
};

/**
 * Drop ONE source document from the playbook.
 *
 * Soft-delete on the backend: the row leaves the GET `source_documents` list,
 * the extracted rules stay. Refetches for the same reason replace does - one
 * read of the truth after the write.
 */
export const removePlaybookSourceDocument = (
  playbookUuid: string,
  documentUuid: string,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(
        `/playbooks/${playbookUuid}/source-documents/${documentUuid}`,
      );

      if (response.status === 200 || response.status === 204) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Document removed from the playbook'),
            modalType: 'success',
            duration: 3000,
          }),
        );
        await dispatch(fetchReviewDetail(playbookUuid, { silent: true }) as any);
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to remove the source document');
    }
    return false;
  };
};

/**
 * What an extraction run does to the rules that are already there.
 *
 * `replace` - the original behaviour: the engine writes the playbook's rules
 * from the submitted documents. `augment` - the engine MERGES what it finds
 * into the existing set; nothing is deleted. Both stream progress over the
 * same `playbook_{uuid}` group, so the live panel is unchanged.
 */
export type PlaybookExtractionMode = 'replace' | 'augment';

/**
 * Upload playbook source documents in batches, then extract rules from them.
 *
 * A playbook is generated from the whole document set TOGETHER, which is why
 * this used to be one multipart POST - and why it was the request most likely
 * to be reset on the EU edge, since it carried every source contract at once.
 *
 * The set is now assembled server-side across several requests and extracted
 * from once. That is what the `upload_id` is for: `/source-documents` mints
 * one on the FIRST batch, every later batch sends it back so its files join
 * the same set, and `/extract` is given it so the engine reads exactly that
 * set. Omitting it anywhere would silently split one playbook's sources into
 * several uploads and extract from a fragment.
 *
 * The endpoint caps a set at 20 documents; anything past it comes back in
 * `skipped` rather than failing the run.
 *
 * @param reviewUuid - UUID of the playbook to add extracted rules to
 * @param files - One or more source documents (documents, spreadsheets, scans)
 * @param mode - `replace` (default) rewrites the rules; `augment` merges into them
 */
export const extractPlaybook = (
  reviewUuid: string,
  files: File | File[],
  mode: PlaybookExtractionMode = 'replace',
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: REVIEW_EXTRACTING_PLAYBOOK, payload: true });

    const stopExtracting = () => dispatch({ type: REVIEW_EXTRACTING_PLAYBOOK, payload: false });

    try {
      const documents = Array.isArray(files) ? files : [files];
      const { accepted, oversized } = partitionOversized(documents);

      if (!accepted.length) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('No source documents could be uploaded'),
            subMsg: oversizedMessage(oversized),
            modalType: 'error',
            duration: 8000,
          }),
        );
        stopExtracting();
        return null;
      }

      const upload = await runSequentialUpload(accepted, {
        send: (batch, ctx) => {
          const formData = new FormData();
          batch.forEach((document) => formData.append('files', document));
          // Omitted on the first batch - that request is what mints it.
          if (ctx.carry) formData.append('upload_id', ctx.carry);
          return apis.post(
            `/playbooks/${reviewUuid}/source-documents`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
        },
        readCarry: (response) => response?.upload_id || response?.uploadId,
      });

      const problems = [
        oversized.length ? oversizedMessage(oversized) : null,
        describeUploadResult(upload),
      ].filter(Boolean) as string[];

      if (upload.uploadedCount === 0) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('No source documents were uploaded, so there is nothing to extract from'),
            subMsg: problems.join(' ') || undefined,
            modalType: 'error',
            duration: 10000,
          }),
        );
        stopExtracting();
        return null;
      }

      const response = await apis.post(
        `/playbooks/${reviewUuid}/extract`,
        {
          // Always sent, including the default: the backend reads one field
          // rather than inferring intent from whether rules already exist.
          mode,
          ...(upload.carry ? { upload_id: upload.carry } : {}),
        },
      );

      if (response.status === 200 || response.status === 202) {
        const count = upload.uploadedCount;
        dispatch(
          showFeedbackModal({
            showModal: true,
            message:
              mode === 'augment'
                ? count > 1
                  ? translate('Adding {{count}} documents to the playbook. New rules will be merged in when complete.', { count })
                  : translate('Adding the document to the playbook. New rules will be merged in when complete.')
                : count > 1
                  ? translate('Generating playbook from {{count}} documents. Rules will be added automatically when complete.', { count })
                  : translate('Playbook extraction started. Rules will be added automatically when complete.'),
            subMsg: problems.join(' ') || undefined,
            modalType: problems.length ? 'warning' : 'success',
            duration: problems.length ? 10000 : 4000,
          }),
        );
        // Keep isExtractingPlaybook=true - the PubSub hook will set it
        // to false once extraction completes or fails. Progress arrives on
        // `playbook_<uuid>`, NOT on a correlation-keyed group, so there is
        // nothing to thread through from this response.
        return response.data;
      }

      stopExtracting();
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to start playbook extraction');
      stopExtracting();
    }

    return null;
  };
};

// ─── Review Sessions List ───────────────────────────────────────────────────

interface FetchReviewSessionsParams {
  page?: number;
  page_size?: number;
  status?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  include_deleted?: boolean;
}

export const fetchReviewSessions = (params: FetchReviewSessionsParams = {}) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    const { page = 1, page_size = 20, status, search, sort_by, sort_order, include_deleted } = params;

    dispatch({ type: REVIEW_LOADING_SESSIONS, payload: true });

    try {
      const response = await apis.get('/reviews', {
        params: {
          page,
          page_size,
          ...(status ? { status } : {}),
          ...(search ? { search } : {}),
          ...(sort_by ? { sort_by } : {}),
          ...(sort_order ? { sort_order } : {}),
          ...(include_deleted ? { include_deleted: 'true' } : {}),
        },
      });

      if (response.status === 200) {
        const data = response.data;
        dispatch({
          type: page === 1 ? REVIEW_FETCH_SESSIONS : REVIEW_APPEND_SESSIONS,
          payload: {
            items: data.items || [],
            total: data.total || 0,
            page: data.page || page,
            total_pages: data.total_pages || 1,
          },
        });
        return data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      dispatch({ type: REVIEW_LOADING_SESSIONS, payload: false });
    }

    return null;
  };
};

// ─── Collaborator Management ─────────────────────────────────────────────────

export const fetchCollaborators = (sessionUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: REVIEW_LOADING_COLLABORATORS, payload: true });

    try {
      const response = await apis.get(`/reviews/${sessionUuid}/collaborators`);
      if (response.status === 200) {
        dispatch({
          type: REVIEW_SET_COLLABORATORS,
          payload: response.data.collaborators || [],
        });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      dispatch({ type: REVIEW_LOADING_COLLABORATORS, payload: false });
    }
    return null;
  };
};

export const addCollaborator = (
  sessionUuid: string,
  payload: AddCollaboratorPayload,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(
        `/reviews/${sessionUuid}/collaborators`,
        payload,
      );

      if (response.status === 201) {
        dispatch({ type: REVIEW_ADD_COLLABORATOR, payload: response.data });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Access granted'),
            modalType: 'success',
            duration: 2000,
          }),
        );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to grant access');
    }
    return null;
  };
};

export const updateCollaborator = (
  sessionUuid: string,
  collaboratorId: string,
  payload: UpdateCollaboratorPayload,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(
        `/reviews/${sessionUuid}/collaborators/${collaboratorId}`,
        payload,
      );

      if (response.status === 200) {
        dispatch({
          type: REVIEW_UPDATE_COLLABORATOR,
          payload: { user_id: collaboratorId, access: response.data.access },
        });
        // dispatch(
        //   showFeedbackModal({
        //     showModal: true,
        //     message: 'Access updated',
        //     modalType: 'success',
        //     duration: 2000,
        //   }),
        // );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update access');
    }
    return null;
  };
};

export const removeCollaborator = (
  sessionUuid: string,
  collaboratorId: string,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(
        `/reviews/${sessionUuid}/collaborators/${collaboratorId}`,
      );

      if (response.status === 200) {
        dispatch({ type: REVIEW_REMOVE_COLLABORATOR, payload: collaboratorId });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Access removed'),
            modalType: 'success',
            duration: 2000,
          }),
        );
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to remove collaborator');
    }
    return false;
  };
};

// ─── Playbook Collaborators (uses /playbooks/ endpoint) ──────────────────────

export const addPlaybookCollaborator = (
  playbookUuid: string,
  payload: AddCollaboratorPayload,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(
        `/playbooks/${playbookUuid}/collaborators`,
        payload,
      );

      if (response.status === 201 || response.status === 200) {
        dispatch({ type: REVIEW_ADD_COLLABORATOR, payload: response.data });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Access granted'),
            modalType: 'success',
            duration: 2000,
          }),
        );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to grant access');
    }
    return null;
  };
};

export const updatePlaybookCollaborator = (
  playbookUuid: string,
  collaboratorId: string,
  payload: UpdateCollaboratorPayload,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(
        `/playbooks/${playbookUuid}/collaborators/${collaboratorId}`,
        payload,
      );

      if (response.status === 200) {
        dispatch({
          type: REVIEW_UPDATE_COLLABORATOR,
          payload: { user_id: collaboratorId, access: response.data.access },
        });
        // dispatch(
        //   showFeedbackModal({
        //     showModal: true,
        //     message: 'Access updated',
        //     modalType: 'success',
        //     duration: 2000,
        //   }),
        // );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update access');
    }
    return null;
  };
};

export const removePlaybookCollaborator = (
  playbookUuid: string,
  collaboratorId: string,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(
        `/playbooks/${playbookUuid}/collaborators/${collaboratorId}`,
      );

      if (response.status === 200) {
        dispatch({ type: REVIEW_REMOVE_COLLABORATOR, payload: collaboratorId });
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Access removed'),
            modalType: 'success',
            duration: 2000,
          }),
        );
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to remove collaborator');
    }
    return false;
  };
};

// ─── Playbook Bulk Delete ────────────────────────────────────────────────────

export const REVIEW_BULK_REMOVE_PLAYBOOKS = 'REVIEW_BULK_REMOVE_PLAYBOOKS';

export const bulkDeletePlaybooks = (uuids: string[], deleteSessions: boolean = false) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/playbooks/bulk-delete', { uuids, delete_sessions: deleteSessions });
      if (response.status === 200) {
        const deletedUuids = response.data.deleted || uuids;
        dispatch({ type: REVIEW_BULK_REMOVE_PLAYBOOKS, payload: deletedUuids });
        const sessionsDeleted = response.data.sessions_deleted || 0;
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: sessionsDeleted > 0
              ? `${response.data.deleted_count || uuids.length} playbook(s) deleted along with ${sessionsDeleted} review session${sessionsDeleted > 1 ? 's' : ''}`
              : `${response.data.deleted_count || uuids.length} playbook(s) deleted`,
            modalType: 'success',
            duration: 3000,
          }),
        );
        return {
          success: true,
          deleted: response.data.deleted_count || uuids.length,
          denied: response.data.denied || [],
        };
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const errorMsg = readErrorMessage(error, '');

      if (status === 403) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: errorMsg || 'You do not have permission to delete these playbooks. Admin access is required.',
            modalType: 'error',
            duration: 5000,
          }),
        );
      } else {
        handleApiError(error, dispatch, 'Failed to delete playbooks');
      }
      return { success: false, error: errorMsg || 'Failed to delete', status };
    }
    return { success: false };
  };
};

// ─── Audit Log ───────────────────────────────────────────────────────────────

interface FetchAuditLogParams {
  page?: number;
  page_size?: number;
  action?: string;
}

export const fetchAuditLog = (
  sessionUuid: string,
  params: FetchAuditLogParams = {},
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    const { page = 1, page_size = 50, action } = params;

    dispatch({ type: REVIEW_LOADING_AUDIT_LOG, payload: true });

    try {
      const response = await apis.get(`/reviews/${sessionUuid}/audit-log`, {
        params: {
          page,
          page_size,
          ...(action ? { action } : {}),
        },
      });

      if (response.status === 200) {
        const data = response.data;
        dispatch({
          type: page === 1 ? REVIEW_SET_AUDIT_LOG : REVIEW_APPEND_AUDIT_LOG,
          payload: {
            items: data.items || [],
            total: data.total || 0,
            page: data.page || page,
            total_pages: data.total_pages || 1,
          },
        });
        return data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      dispatch({ type: REVIEW_LOADING_AUDIT_LOG, payload: false });
    }
    return null;
  };
};

// ─── Document Viewer ─────────────────────────────────────────────────────────

export const fetchDocumentContent = (
  sessionUuid: string,
  documentId: number,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: REVIEW_LOADING_DOCUMENT, payload: true });

    try {
      const response = await apis.get(
        `/reviews/${sessionUuid}/documents/${documentId}`,
      );

      if (response.status === 200) {
        dispatch({ type: REVIEW_SET_DOCUMENT_CONTENT, payload: response.data });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to load document');
    } finally {
      dispatch({ type: REVIEW_LOADING_DOCUMENT, payload: false });
    }
    return null;
  };
};

/**
 * Stream a review document's decrypted binary directly from the backend.
 * Returns a Blob that can be used with URL.createObjectURL for inline viewing.
 */
export const streamReviewDocument = (
  sessionUuid: string,
  documentId: number,
  disposition: 'inline' | 'attachment' = 'inline',
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis({
        method: 'POST',
        url: `/reviews/${sessionUuid}/documents/${documentId}/stream`,
        data: { disposition },
        responseType: 'blob',
      });

      if (response.status === 200) {
        const contentType =
          response.headers['content-type'] || 'application/octet-stream';
        return { success: true, blob: response.data as Blob, contentType };
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to stream document');
    }
    return { success: false, blob: null, contentType: '' };
  };
};

/**
 * Stream a revised document (clean or redline) with applied finding decisions.
 * Returns a Blob identical to streamReviewDocument but from the revised copy.
 */
export const streamRevisedDocument = (
  sessionUuid: string,
  documentId: number,
  mode: 'clean' | 'redline' = 'clean',
  disposition: 'inline' | 'attachment' = 'inline',
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis({
        method: 'POST',
        url: `/reviews/${sessionUuid}/documents/${documentId}/stream-revision`,
        data: { mode, disposition },
        responseType: 'blob',
      });

      if (response.status === 200) {
        const contentType =
          response.headers['content-type'] || 'application/octet-stream';
        return { success: true, blob: response.data as Blob, contentType };
      }
    } catch (error) {
      // 404 = no revision available yet (no accepted/modified findings)
      const axiosErr = error as any;
      if (axiosErr?.response?.status === 404) {
        return { success: false, blob: null, contentType: '', noRevision: true };
      }
      handleApiError(error, dispatch, 'Failed to stream revised document');
    }
    return { success: false, blob: null, contentType: '' };
  };
};

// ─── Re-review ───────────────────────────────────────────────────────────────

export const startReReview = (
  sessionUuid: string,
  payload: StartReviewPayload,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: REVIEW_SET_UPLOADING, payload: true });

    try {
      const hasFiles = payload.files && payload.files.length > 0;
      let response;

      if (hasFiles) {
        // New documents provided - send as multipart/form-data
        const formData = new FormData();
        payload.files.forEach((file) => formData.append('files', file));
        if (payload.name?.trim()) {
          formData.append('name', payload.name.trim());
        }
        if (payload.jurisdiction?.length) {
          formData.append('jurisdiction', JSON.stringify(payload.jurisdiction));
        }
        if (payload.matter_uuid) {
          formData.append('matter_uuid', payload.matter_uuid);
        }

        response = await apis.post(
          `/reviews/${sessionUuid}/re-review`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
      } else {
        // No new documents - reuse parent's documents, send as JSON
        const jsonBody: Record<string, any> = {};
        if (payload.name?.trim()) jsonBody.name = payload.name.trim();
        if (payload.jurisdiction?.length) jsonBody.jurisdiction = payload.jurisdiction;
        if (payload.matter_uuid) jsonBody.matter_uuid = payload.matter_uuid;

        response = await apis.post(
          `/reviews/${sessionUuid}/re-review`,
          jsonBody,
        );
      }

      if (response.status === 201) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Re-review started - documents are being processed'),
            modalType: 'success',
            duration: 4000,
          }),
        );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to start re-review');
    } finally {
      dispatch({ type: REVIEW_SET_UPLOADING, payload: false });
    }
    return null;
  };
};

// ─── Sign-Off & Finalization ─────────────────────────────────────────────────────

export const completeReview = (sessionUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/reviews/${sessionUuid}/complete`);
      if (response.status === 200) {
        dispatch({
          type: REVIEW_SESSION_STATUS_CHANGE,
          payload: { status: 'pending_sign_off' },
        });
        dispatch(fetchSignOffStatus(sessionUuid) as any);
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Review marked as complete - awaiting sign-off'),
            modalType: 'success',
            duration: 4000,
          }),
        );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to complete review');
    }
    return null;
  };
};

export const fetchSignOffStatus = (sessionUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: REVIEW_LOADING_SIGN_OFF, payload: true });
    try {
      const response = await apis.get(`/reviews/${sessionUuid}/sign-off-status`);
      if (response.status === 200) {
        dispatch({ type: REVIEW_SET_SIGN_OFF_STATUS, payload: response.data });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to load sign-off status');
    } finally {
      dispatch({ type: REVIEW_LOADING_SIGN_OFF, payload: false });
    }
    return null;
  };
};

export const submitSignOff = (sessionUuid: string, payload: SubmitSignOffPayload = {}) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/reviews/${sessionUuid}/sign-off`, payload);
      if (response.status === 200) {
        // Refresh sign-off status
        dispatch(fetchSignOffStatus(sessionUuid) as any);

        // If review was finalized, update session status
        if (response.data.status === 'finalized') {
          dispatch({
            type: REVIEW_SESSION_STATUS_CHANGE,
            payload: {
              status: 'finalized',
              finalized_at: response.data.finalized_at,
            },
          });
          dispatch(
            showFeedbackModal({
              showModal: true,
              message: translate('Review finalized - all signers have approved'),
              modalType: 'success',
              duration: 4000,
            }),
          );
        } else {
          dispatch(
            showFeedbackModal({
              showModal: true,
              message: translate('Sign-off recorded'),
              modalType: 'success',
              duration: 3000,
            }),
          );
        }

        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to submit sign-off');
    }
    return null;
  };
};

export const reopenReview = (sessionUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/reviews/${sessionUuid}/reopen`);
      if (response.status === 200) {
        dispatch({
          type: REVIEW_SESSION_STATUS_CHANGE,
          payload: { status: 'pending_sign_off', finalized_at: null },
        });
        dispatch(fetchSignOffStatus(sessionUuid) as any);
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate('Review reopened - sign-offs have been cleared'),
            modalType: 'success',
            duration: 4000,
          }),
        );
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to reopen review');
    }
    return null;
  };
};

export const downloadFinalDocuments = (sessionUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(
        `/reviews/${sessionUuid}/download-final`,
        {},
        { responseType: 'blob' },
      );

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: 'application/zip' });
        const contentDisposition = response.headers['content-disposition'] || '';
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        const filename = filenameMatch?.[1] || 'review_final.zip';

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true };
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to download final documents');
    }
    return { success: false };
  };
};
